import { createFileRoute, useRouter } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useRef } from "react";
import { listMedia, deleteMedia } from "@/lib/api/media.functions";
import { mediaUploadResultMessage, uploadMediaFiles } from "@/lib/media/upload";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export const Route = createFileRoute("/admin/media/")({
  loader: () => listMedia(),
  component: AdminMediaPage,
});

type Row = Awaited<ReturnType<typeof listMedia>>[number];

function AdminMediaPage() {
  const files = Route.useLoaderData();
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  async function handleUpload(fileList: FileList | null) {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length === 0) return;

    const result = await uploadMediaFiles(files);
    if (result.uploaded.length > 0) {
      toast.success(mediaUploadResultMessage(result));
      router.invalidate();
    } else {
      toast.error(mediaUploadResultMessage(result));
    }
    if (result.failed.length > 0 && result.uploaded.length > 0) {
      toast.error(
        result.failed.map((f) => `${f.filename}: ${f.error}`).join("; "),
        { duration: 8000 },
      );
    }
  }

  const columns: ColumnDef<Row>[] = [
    {
      id: "preview",
      header: "Preview",
      enableSorting: false,
      cell: ({ row }) => (
        <img
          src={row.original.path}
          alt={row.original.altText ?? row.original.filename}
          className="h-12 w-12 rounded object-cover"
        />
      ),
    },
    { accessorKey: "filename", header: "Filename" },
    {
      accessorKey: "mimeType",
      header: "Type",
      meta: { filterVariant: "select", groupable: true },
    },
    {
      id: "size",
      header: "Size",
      accessorFn: (row) => row.size,
      cell: ({ row }) => `${(row.original.size / 1024).toFixed(0)} KB`,
    },
    {
      accessorKey: "createdAt",
      header: "Uploaded",
      cell: ({ row }) =>
        row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : "",
    },
    {
      ...tableActionsColumn<Row>(),
      cell: ({ row }) => (
        <TableRowActions
          showEdit={false}
          onDelete={async () => {
            await deleteMedia({ data: { id: row.original.id } });
            toast.success("Deleted");
            router.invalidate();
          }}
          deleteLabel={row.original.filename}
        />
      ),
    },
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Media library"
        action={
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                void handleUpload(e.target.files);
                e.target.value = "";
              }}
            />
            <Button onClick={() => inputRef.current?.click()}>
              <Upload className="mr-2 h-4 w-4" /> Upload images
            </Button>
          </>
        }
      />
      <DataTable tableId="media" columns={columns} data={files} />
    </AdminPageShell>
  );
}
